import clsx from 'clsx';
import {
  calculateSoftSkillWeightsSum,
  ProfileSkillsTabsValues,
} from 'components/feature/jobRole/JobRoleForm/JobRoleForm.helpers';
import { TJobRoleFormValues } from 'components/feature/jobRole/JobRoleForm/JobRoleForm.types';
import FormSoftSkillFields from 'components/feature/jobRole/JobRoleFormSoftSkillFields/JobRoleFormSoftSkillFields';
import TechincalSkillsFields from 'components/feature/jobRole/TechincalSkillsFields/TechincalSkillsFields';
import { CompanyFormikSelectFormField } from 'components/molecules/SelectFormField/CompanySelectFormField/CompanySelectFormField';
import { jobFamilyOptions, roleLevelOptions } from 'constants/roleLevel';
import { Form, useField, useFormikContext } from 'formik';
import { SoftSkillFragment } from 'generated/graphql';
import { FC, useMemo } from 'react';
import {
  Button,
  FormikCheckboxFormField,
  FormikSelectFormField,
  FormikTextInputField,
  TabListLegacy as TabList,
  TabPanelLegacy as TabPanel,
  Tooltip,
} from '@spotted-zebra-uk/ui-components';
import Icon from '../../../atoms/Icon';
import styles from './JobRoleFormFields.module.scss';

interface IFormFields {
  traitsModalSoftSkillIndex?: number;
  useCustomWeightsVisible?: boolean;
  disableRoleLevelField?: boolean;
  onOpenTraitsModal: (traitsModalSoftSkillIndex: number) => void;
  onCloseTraitsModal: () => void;
  onCancel: () => void;
  predefinedSoftSkills?: SoftSkillFragment[];
}

const companyFieldTooltipContent = () => (
  <>
    <div style={{ textAlign: 'center' }}>Company owner cannot be</div>
    <div style={{ textAlign: 'center' }}>changed as this role is already</div>
    <div style={{ textAlign: 'center' }}>attached to a project.</div>
  </>
);

const FormFields: FC<IFormFields> = ({
  traitsModalSoftSkillIndex,
  useCustomWeightsVisible = true,
  disableRoleLevelField = false,
  onCloseTraitsModal,
  onOpenTraitsModal,
  onCancel,
  predefinedSoftSkills,
}) => {
  const { handleSubmit, values } = useFormikContext<TJobRoleFormValues>();

  const weightSum = calculateSoftSkillWeightsSum(values.softSkills);

  const numberOfSoftSkills = useMemo(
    () => values.softSkills.filter(skill => skill.name).length,
    [values.softSkills]
  );
  const numberOfTechnicalSkills = useMemo(
    () => values.technicalSkills.filter(skill => skill.value).length,
    [values.technicalSkills]
  );
  const tabsOptions = [
    {
      title: 'Soft skills',
      value: ProfileSkillsTabsValues.SOFT_SKILLS,
      additionalInfo: numberOfSoftSkills,
    },
    {
      title: 'Technical skills',
      value: ProfileSkillsTabsValues.TECHNICAL_SKILLS,
      additionalInfo: numberOfTechnicalSkills,
    },
  ];

  const weightSumString = `Weights sum: ${weightSum.toLocaleString()}`;

  return (
    <Form noValidate onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.firstFieldsRow} data-testid="job-role-form-fields">
        <div className={styles.fieldWrapper}>
          <FormikTextInputField
            id="jobRoleName"
            label="Name"
            data-role="job-role-name-input"
            data-testid="job-role-name-input"
            placeholder="Name"
            useFormikField={useField}
          />
        </div>

        <div className={styles.fieldWrapper}>
          <Tooltip render={companyFieldTooltipContent}>
            <div>
              <div data-testid="company-select-field-wrapper">
                <CompanyFormikSelectFormField
                  id="company"
                  label="Company"
                  placeholder="Company"
                  maxMenuHeight={240}
                  name="company"
                  isDisabled
                />
              </div>
            </div>
          </Tooltip>
        </div>

        <div className={styles.fieldWrapper}>
          <FormikTextInputField
            id="additionalInformation"
            label="Additional information"
            data-role="job-role-additional-information-input"
            placeholder="Additional information"
            data-testid="job-role-additional-information-input"
            useFormikField={useField}
          />
        </div>
      </div>
      <div className={styles.secondFieldsRow}>
        <div
          className={styles.fieldWrapper}
          data-testid="role-level-select-wrapper"
        >
          <FormikSelectFormField
            id="roleLevel"
            label="Role level"
            options={roleLevelOptions}
            data-role="role-level-select"
            placeholder="Role level"
            isDisabled={disableRoleLevelField}
            maxMenuHeight={240}
            selectProps={{
              'data-testid': 'role-level-select',
            }}
            useFormikField={useField}
            hasClearIndicator={false}
          />
        </div>
        <div
          className={styles.jobFamilyField}
          data-testid="job-family-select-wrapper"
        >
          <FormikSelectFormField
            id="family"
            label="Job Family (Right for role)"
            options={jobFamilyOptions}
            data-role="job-family-select"
            placeholder="Job Family"
            maxMenuHeight={240}
            selectProps={{
              'data-testid': 'job-family-select',
            }}
            useFormikField={useField}
            hasClearIndicator={false}
          />
          <Tooltip
            content="Skills for 'Right for us' and 'Right for the future' are benchmarked globally, not tailored to specific job families."
            id="tooltip-trigger"
          >
            <Icon
              icon="info"
              id="tooltip-trigger"
              className={styles.infoIcon}
            />
          </Tooltip>
        </div>
        {useCustomWeightsVisible && (
          <div className={styles.useCustomWeightsCheckboxWrapper}>
            <FormikCheckboxFormField
              id="customWeights"
              label="Use Custom Weights"
              data-role="custom-weights-check"
              data-testid="custom-weights-check"
              useFormikField={useField}
            />
          </div>
        )}
        <div
          className={clsx(
            styles.customWeightLabel,
            weightSum !== 100 && styles.customWeightLabel_error
          )}
        >
          {values.customWeights && <span>{weightSumString}</span>}
        </div>
      </div>

      <div className={styles.skillsWrapper}>
        <TabList
          initialValue={ProfileSkillsTabsValues.SOFT_SKILLS}
          options={tabsOptions}
        >
          <TabPanel value={ProfileSkillsTabsValues.SOFT_SKILLS}>
            <FormSoftSkillFields
              openTraitsModal={onOpenTraitsModal}
              onCloseTraitsModal={onCloseTraitsModal}
              traitsModalSoftSkillIndex={traitsModalSoftSkillIndex}
              predefinedSoftSkills={predefinedSoftSkills}
            />
          </TabPanel>
          <TabPanel value={ProfileSkillsTabsValues.TECHNICAL_SKILLS}>
            <TechincalSkillsFields />
          </TabPanel>
        </TabList>
      </div>
      <div className={styles.actions}>
        <Button
          onClick={onCancel}
          type="button"
          variant="secondary"
          size="medium"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          data-role="save-skills-profile"
          data-testid="save-form"
          size="medium"
          disabled={!values.jobRoleName}
        >
          Save
        </Button>
      </div>
    </Form>
  );
};

export default FormFields;
